import classNames from 'classnames';
import { defaultButtonTitle, widgetStateToggleUrlParam } from 'constants/general';
import { AnalyticsTarget } from 'enums/AnalyticsTarget';
import { EHash } from 'enums/UrlHash';
import { useInAuthZone } from 'hooks/useInAuthZone';
import { useIsHistoryApiEnabled } from 'hooks/useIsHistoryApiEnabled';
import { useSearchParams } from 'hooks/useSearchParams';
import { useWidgetParameters } from 'hooks/useWidgetParameters';
import React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { services } from 'services/container';
import { setChatOpen, updateContextMenu } from 'state/chat/chat.actions';
import CrossIcon from 'svg/24/cross.svg';
import ChatIcon from 'svg/40/chat-icon.svg';
import { createReachGoalActionBy } from 'utils/analytics';
import { ChatStateController } from 'utils/chatState';

import styles from './WidgetToggleButton.less';

const originalBodyStyles: { height: string; overflowY: string } = {
    height: document.body.style.height,
    overflowY: document.body.style.overflowY,
};

export const WidgetToggleButton: React.FC<{ showOverlap?: boolean }> = ({ showOverlap = true }) => {
    const dispatch = useDispatch();
    const inAuthZone = useInAuthZone();

    const handleBackButtonPressed = useCallback(() => {
        services.widget.eventChannel.emit('forceUpdateSearchParamsDependency', false);

        if (window.location.hash !== `#${EHash.ContextMenu}`) {
            dispatch(updateContextMenu(null));
        }
    }, []);

    useEffect(() => {
        window.addEventListener('popstate', handleBackButtonPressed, false);

        return () => {
            window.removeEventListener('popstate', handleBackButtonPressed);
        };
    }, []);

    const isHistoryApiEnabled = useIsHistoryApiEnabled();
    const open = useSelector((state) => state.chat.open);
    const setOpen = (open: boolean) => dispatch(setChatOpen(open));

    const [searchParams] = useSearchParams(widgetStateToggleUrlParam);
    const isChatOpenRaw = searchParams.get(widgetStateToggleUrlParam);
    const isChatOpen = useMemo(() => {
        if (!isHistoryApiEnabled) {
            return open;
        }

        return isChatOpenRaw === `"1"`;
    }, [isHistoryApiEnabled, isChatOpenRaw, open]);

    const unreadCount = useSelector((state) => state.chat.unreadCounter);

    const displayedUnreadCount =
        unreadCount > 0 ? (unreadCount > 9 ? '9+' : String(unreadCount)) : null;

    const buttonClass = classNames(styles.chatButton, {
        [styles.chatButton_open]: open,
        [styles.chatButton_closed]: !open,
    });

    const { displaySettings } = useWidgetParameters();

    const onClick = useCallback(() => {
        services.analytics.sendAnalyticsAction('Клик по «Чат с банком»');
        if (!inAuthZone) {
            services.analytics.send(
                createReachGoalActionBy(AnalyticsTarget.CLICK_BUTTON_UNAUTHORIZED_WIDGET),
            );
        }

        if (isChatOpen) {
            return handleClose();
        }

        ChatStateController.toggleChat(true);
    }, [inAuthZone, isChatOpen]);

    const handleClose = useCallback(() => {
        ChatStateController.toggleChat(false);
    }, []);

    useEffect(() => {
        setOpen(isChatOpen);

        // document.body.style.overflowY = isChatOpen ? 'hidden' : originalBodyStyles.overflowY;
        document.body.style.height = isChatOpen ? '100vh' : originalBodyStyles.height;
    }, [isChatOpen]);

    useEffect(() => {
        if (typeof isChatOpenRaw !== 'undefined' && !isChatOpen) {
            ChatStateController.toggleChat(false);
        }
    }, []);

    const onMouseEnter = () => {
        services.analytics.sendAnalyticsAction('Наведение на «Чат с банком»');
    };

    const showBadge = !displaySettings?.hideBadge;
    const buttonTitle = displaySettings?.ButtonTitle ?? defaultButtonTitle;

    return (
        <>
            {showOverlap && isChatOpen && (
                <div
                    className={styles.widgetBackground}
                    data-test-id="webchat_chat_background"
                    role="none"
                    tabIndex={-1}
                    onClick={handleClose}
                />
            )}
            {showBadge && (
                <button
                    className={buttonClass}
                    title={`${open ? 'Свернуть' : 'Открыть'} ${buttonTitle}`}
                    data-test-id="webchat_toggleButton"
                    data-chat-open={open}
                    role="button"
                    aria-expanded={open}
                    onClick={onClick}
                    onMouseEnter={onMouseEnter}
                >
                    <span className={styles.iconWrapper}>
                        {open ? (
                            <CrossIcon className={styles.icon} />
                        ) : (
                            <ChatIcon className={styles.icon} />
                        )}
                    </span>
                    <span className={styles.badgeCaption} data-test-id="webchat_toggleButton_title">
                        {buttonTitle}
                    </span>
                    {!open && displayedUnreadCount && (
                        <span
                            className={styles.unreadCounter}
                            title={`Непрочитанных сообщений: ${displayedUnreadCount}`}
                        >
                            {displayedUnreadCount}
                        </span>
                    )}
                </button>
            )}
        </>
    );
};
